import { useState, useEffect, useRef, useCallback } from 'react';

const DEFAULT_BOOKING_URL = 'https://getsquire.com/booking/brands/bro-code-barbershop';

const DEFAULT_IMAGES = {
  before: 'https://brocode.b-cdn.net/Screenshot%202026-03-15%20at%2012.09.38%E2%80%AFPM.png',
  after: 'https://brocode.b-cdn.net/hf_20260315_193425_f9b645e9-d929-4d2d-b0cf-15a2e106c8c1.jpeg',
};

const DEFAULT_HERO_WORDS = ['SHARP FADES', 'CLEAN LINES', 'FRESH CUTS', 'REAL STYLE'];

const BeforeAfterSlider = ({ beforeSrc, afterSrc }) => {
  const containerRef = useRef(null);
  const [sliderPos, setSliderPos] = useState(0); // Start at top (showing "before")
  const [isDragging, setIsDragging] = useState(false);
  const [mode, setMode] = useState('vertical-auto'); // 'vertical-auto' | 'horizontal-user'
  const [isTransitioning, setIsTransitioning] = useState(false);
  const rafRef = useRef(null);
  const pendingPosRef = useRef(null);

  // Auto-play vertical intro animation (before → after × 2)
  useEffect(() => {
    if (mode !== 'vertical-auto') return;

    const timeline = [
      { delay: 600, position: 95 },   // First reveal: slide down to "after"
      { delay: 1200, position: 5 },   // Back up to "before"
      { delay: 1000, position: 95 },  // Second reveal: slide down to "after"
      { delay: 1200, position: 5 },   // Back up to "before"
      { delay: 800, position: 50 },   // Center before transition
    ];

    let timeouts = [];
    let cumulativeDelay = 0;

    timeline.forEach(({ delay, position }) => {
      cumulativeDelay += delay;
      const timeout = setTimeout(() => {
        setSliderPos(position);
      }, cumulativeDelay);
      timeouts.push(timeout);
    });

    // Transition to horizontal mode
    const transitionTimeout = setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setMode('horizontal-user');
        setSliderPos(50); // Reset to center for horizontal
        setIsTransitioning(false);
      }, 400);
    }, cumulativeDelay + 600);
    timeouts.push(transitionTimeout);

    return () => timeouts.forEach(clearTimeout);
  }, [mode]);

  // RAF-based position update for smooth dragging
  const updatePosition = useCallback((clientX) => {
    if (!containerRef.current || mode !== 'horizontal-user') return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    // Store pending position and schedule RAF update
    pendingPosRef.current = percent;
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        if (pendingPosRef.current !== null) {
          setSliderPos(pendingPosRef.current);
        }
        rafRef.current = null;
      });
    }
  }, [mode]);

  const handleMouseDown = (e) => {
    if (mode !== 'horizontal-user') return;
    e.preventDefault();
    setIsDragging(true);
    // Immediate update on click (no RAF delay for initial position)
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      setSliderPos(Math.max(0, Math.min(100, (x / rect.width) * 100)));
    }
  };

  const handleTouchStart = (e) => {
    if (mode !== 'horizontal-user') return;
    setIsDragging(true);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      setSliderPos(Math.max(0, Math.min(100, (x / rect.width) * 100)));
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || mode !== 'horizontal-user') return;
      updatePosition(e.clientX);
    };

    const handleTouchMove = (e) => {
      if (!isDragging || mode !== 'horizontal-user') return;
      e.preventDefault(); // Prevent scroll while dragging
      updatePosition(e.touches[0].clientX);
    };

    const handleEnd = () => {
      setIsDragging(false);
      // Cancel any pending RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, mode, updatePosition]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const isVertical = mode === 'vertical-auto';
  const isHorizontal = mode === 'horizontal-user';

  // Clip paths based on mode
  const clipPath = isVertical
    ? `inset(0 0 ${100 - sliderPos}% 0)` // Vertical: clip from bottom
    : `inset(0 0 0 ${sliderPos}%)`; // Horizontal: clip from left (before on right)

  // Transition timing - no transition during horizontal drag for instant response
  const transitionDuration = isVertical ? '0.7s' : (isDragging ? '0s' : '0.15s');
  const transitionTiming = isVertical ? 'cubic-bezier(0.4, 0, 0.2, 1)' : 'ease-out';

  return (
    <div
      ref={containerRef}
      style={{
        ...sliderStyles.container,
        cursor: isHorizontal ? 'ew-resize' : 'default',
        opacity: isTransitioning ? 0.8 : 1,
        transition: 'opacity 0.3s ease',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Before Image (Base Layer) — above fold, high priority */}
      <img src={beforeSrc} alt="Client before haircut at Bro Code Barbershop" width="760" height="1000" fetchPriority="high" decoding="async"
        style={{ ...sliderStyles.image, filter: 'brightness(0.85) grayscale(0.2)' }} />

      {/* After Image (Clipped Layer) */}
      <div
        style={{
          ...sliderStyles.clipLayer,
          clipPath,
          transition: isDragging ? 'none' : `clip-path ${transitionDuration} ${transitionTiming}`,
        }}
      >
        <img src={afterSrc} alt="Client after a fresh fade and haircut at Bro Code Barbershop" width="760" height="1000" fetchPriority="high" decoding="async"
          style={sliderStyles.image} />
      </div>

      {/* Slider Handle */}
      {isVertical ? (
        // Vertical handle (horizontal line)
        <div
          style={{
            ...sliderStyles.handleVertical,
            top: `${sliderPos}%`,
            transition: `top ${transitionDuration} ${transitionTiming}`,
          }}
        >
          <div style={sliderStyles.handleLineHorizontal} />
          <div style={sliderStyles.handleKnob}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M5 8l7-5 7 5M5 16l7 5 7-5" stroke="#0C0C0C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      ) : (
        // Horizontal handle (vertical line)
        <div
          style={{
            ...sliderStyles.handleHorizontal,
            left: `${sliderPos}%`,
            transition: isDragging ? 'none' : 'left 0.15s ease-out',
          }}
        >
          <div style={sliderStyles.handleLineVertical} />
          <div
            style={{
              ...sliderStyles.handleKnob,
              transform: isDragging ? 'translate(-50%, -50%) scale(1.1)' : 'translate(-50%, -50%) scale(1)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M8 5l-5 7 5 7M16 5l5 7-5 7" stroke="#0C0C0C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      )}

      {/* Labels */}
      {isVertical ? (
        <>
          <div style={{ ...sliderStyles.label, top: '20px', left: '50%', transform: 'translateX(-50%)', opacity: sliderPos > 15 ? 1 : 0 }}>BEFORE</div>
          <div style={{ ...sliderStyles.label, bottom: '20px', left: '50%', transform: 'translateX(-50%)', opacity: sliderPos < 85 ? 1 : 0 }}>AFTER</div>
        </>
      ) : (
        <>
          <div style={{ ...sliderStyles.label, top: '50%', left: '20px', transform: 'translateY(-50%)', opacity: sliderPos > 15 ? 1 : 0 }}>BEFORE</div>
          <div style={{ ...sliderStyles.label, top: '50%', right: '20px', transform: 'translateY(-50%)', opacity: sliderPos < 85 ? 1 : 0 }}>AFTER</div>
        </>
      )}

      {/* Instruction hint - shows after transition to horizontal */}
      {isHorizontal && (
        <div style={{ ...sliderStyles.hint, opacity: isDragging ? 0 : 1 }}>
          <span>← Drag to compare →</span>
        </div>
      )}

      {/* Intro animation indicator */}
      {isVertical && (
        <div style={sliderStyles.introIndicator}>
          <div style={sliderStyles.introPulse} />
        </div>
      )}
    </div>
  );
};

const sliderStyles = {
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '8px',
    userSelect: 'none',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    pointerEvents: 'none',
  },
  clipLayer: {
    position: 'absolute',
    inset: 0,
    willChange: 'clip-path',
  },
  // Vertical mode handle (moves up/down)
  handleVertical: {
    position: 'absolute',
    left: 0,
    right: 0,
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  handleLineHorizontal: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, transparent 0%, #C6A24A 10%, #C6A24A 90%, transparent 100%)',
    boxShadow: '0 0 20px rgba(198, 162, 74, 0.6), 0 0 40px rgba(198, 162, 74, 0.3)',
  },
  // Horizontal mode handle (moves left/right)
  handleHorizontal: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    willChange: 'left',
  },
  handleLineVertical: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '3px',
    background: 'linear-gradient(180deg, transparent 0%, #C6A24A 10%, #C6A24A 90%, transparent 100%)',
    boxShadow: '0 0 20px rgba(198, 162, 74, 0.6), 0 0 40px rgba(198, 162, 74, 0.3)',
  },
  handleKnob: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #C6A24A 0%, #a8893d 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(198, 162, 74, 0.5), 0 0 0 3px rgba(255,255,255,0.1)',
    transition: 'transform 0.2s ease',
    zIndex: 2,
  },
  label: {
    position: 'absolute',
    fontFamily: "'Oswald', sans-serif",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '3px',
    color: '#C6A24A',
    textShadow: '0 2px 10px rgba(0,0,0,0.8)',
    transition: 'opacity 0.3s ease',
    pointerEvents: 'none',
  },
  hint: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    fontFamily: "'Oswald', sans-serif",
    fontSize: '12px',
    letterSpacing: '2px',
    color: 'rgba(255,255,255,0.5)',
    textShadow: '0 2px 10px rgba(0,0,0,0.8)',
    transition: 'opacity 0.3s ease',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  },
  introIndicator: {
    position: 'absolute',
    bottom: '24px',
    right: '24px',
    width: '12px',
    height: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  introPulse: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#C6A24A',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
};

// Add keyframes for pulse animation via style injection
if (typeof document !== 'undefined' && !document.getElementById('slider-animations')) {
  const style = document.createElement('style');
  style.id = 'slider-animations';
  style.textContent = `
    @keyframes pulse {
      0%, 100% { opacity: 0.4; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.3); }
    }
  `;
  document.head.appendChild(style);
}

const Hero = ({ heroContent, teamCount }) => {
  const bookingUrl = heroContent?.bookingUrl || DEFAULT_BOOKING_URL;
  const heroWords = (heroContent?.heroWords?.length ? heroContent.heroWords : DEFAULT_HERO_WORDS);
  const tagline = heroContent?.tagline || "FRASER VALLEY'S PREMIER BARBERSHOP";
  const description = heroContent?.description || "Award-winning barbers in Abbotsford & Surrey and the Fraser Valley delivering precision fades, classic cuts, and modern styles. Where craftsmanship meets confidence.";
  const beforeSrc = heroContent?.beforeImage || DEFAULT_IMAGES.before;
  const afterSrc = heroContent?.afterImage || DEFAULT_IMAGES.after;
  const ctaPrimary = heroContent?.ctaPrimary || 'BOOK YOUR CUT';
  const ctaSecondary = heroContent?.ctaSecondary || 'VIEW SERVICES';
  const trustStats = heroContent?.trustStats || [
    { number: '400+', label: '5-Star Reviews' },
    { number: '2',    label: 'Locations' },
    { number: '8',    label: 'Team Members' },
  ];
  const currentStats = trustStats.map(stat =>
    ['Expert Barbers', 'Team Members'].includes(stat.label) && teamCount != null
      ? { number: String(teamCount), label: 'Team Members' }
      : stat
  );

  const [isMobile, setIsMobile] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);
  const [wordVisible, setWordVisible] = useState(true);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const wordTimer = setInterval(() => {
      setWordVisible(false);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % heroWords.length);
        setWordVisible(true);
      }, 400);
    }, 2800);
    return () => clearInterval(wordTimer);
  }, [heroWords.length]);

  return (
    <section style={styles.hero}>
      {/* Subtle grain overlay */}
      <div style={styles.grain} />

      <div style={styles.container}>
        {/* Text Content */}
        <div style={{ ...styles.textContent, maxWidth: isMobile ? '100%' : '55%' }}>
          <span style={styles.tagline}>{tagline}</span>

          <h1 style={{ ...styles.headline, fontSize: isMobile ? 'clamp(2.5rem, 12vw, 4rem)' : 'clamp(3.5rem, 8vw, 7rem)' }}>
            THE BEST<br />
            BARBERSHOP FOR<br />
            <span
              style={{
                ...styles.accentWord,
                opacity: wordVisible ? 1 : 0,
                transform: wordVisible ? 'translateY(0)' : 'translateY(10px)',
              }}
            >
              {heroWords[wordIndex % heroWords.length]}
            </span>
          </h1>

          <p style={{ ...styles.description, fontSize: isMobile ? '1rem' : '1.15rem', margin: isMobile ? '24px auto 0' : '32px 0 0' }}>
            {description}
          </p>

          {/* Mobile: Slider appears here, above the buttons */}
          {isMobile && (
            <div
              style={{
                width: '100%',
                height: '45vh',
                marginTop: '32px',
                marginBottom: '8px',
              }}
            >
              <BeforeAfterSlider beforeSrc={beforeSrc} afterSrc={afterSrc} />
            </div>
          )}

          <div style={{ ...styles.ctaGroup, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
            <button
  onClick={() => {
    window.gtag?.('event', 'conversion', {
      'send_to': 'AW-11480037647/YvQ4CIOw3I0cEI_yjeIq'
    });
    window.open(bookingUrl, '_blank');
  }}
  style={styles.ctaPrimary}
  onMouseEnter={(e) => {
    e.target.style.background = 'linear-gradient(135deg, #d4af5a 0%, #C6A24A 100%)';
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 8px 32px rgba(198, 162, 74, 0.4)';
  }}
  onMouseLeave={(e) => {
    e.target.style.background = 'linear-gradient(135deg, #C6A24A 0%, #a8893d 100%)';
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 4px 20px rgba(198, 162, 74, 0.3)';
  }}
>
              {ctaPrimary}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: '10px' }}>
                <path d="M3 8h10M10 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <a href="#services" style={styles.ctaSecondary}>
              {ctaSecondary}
            </a>
          </div>

          {/* Trust indicators */}
          <div style={styles.trustRow}>
            {currentStats.map((stat, i) => (
              <div key={i} style={{ display: 'contents' }}>
                {i > 0 && <div style={styles.trustDivider} />}
                <div style={styles.trustItem}>
                  <span style={styles.trustNumber}>{stat.number}</span>
                  <span style={styles.trustLabel}>{stat.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: Before/After Slider positioned absolutely on the right */}
        {!isMobile && (
          <div
            style={{
              ...styles.sliderWrapper,
              position: 'absolute',
              right: '5vw',
              width: '38vw',
              height: '75vh',
            }}
          >
            <BeforeAfterSlider beforeSrc={beforeSrc} afterSrc={afterSrc} />
          </div>
        )}
      </div>
    </section>
  );
};

const styles = {
  hero: {
    position: 'relative',
    minHeight: '100vh',
    background: '#0C0C0C',
    display: 'flex',
    alignItems: 'center',
    padding: '120px 0 80px',
    overflow: 'hidden',
  },
  grain: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 0.03,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
  },
  container: {
    position: 'relative',
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 5vw',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  textContent: {
    position: 'relative',
    zIndex: 2,
  },
  tagline: {
    display: 'inline-block',
    fontFamily: "'Oswald', sans-serif",
    fontSize: '0.75rem',
    fontWeight: 500,
    letterSpacing: '0.25em',
    color: '#C6A24A',
    marginBottom: '1.5rem',
    padding: '8px 16px',
    border: '1px solid rgba(198, 162, 74, 0.3)',
    borderRadius: '4px',
  },
  headline: {
    fontFamily: "'Oswald', 'Impact', sans-serif",
    fontWeight: 700,
    lineHeight: 0.95,
    letterSpacing: '-0.03em',
    color: '#fff',
    textTransform: 'uppercase',
    margin: 0,
  },
  accentWord: {
    display: 'inline-block',
    color: '#C6A24A',
    transition: 'opacity 0.4s ease, transform 0.4s ease',
  },
  description: {
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 1.7,
    maxWidth: '480px',
  },
  ctaGroup: {
    display: 'flex',
    gap: '16px',
    marginTop: '40px',
  },
  ctaPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 32px',
    background: 'linear-gradient(135deg, #C6A24A 0%, #a8893d 100%)',
    color: '#0C0C0C',
    border: 'none',
    borderRadius: '50px',
    cursor: 'pointer',
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 600,
    fontSize: '0.9rem',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    boxShadow: '0 4px 20px rgba(198, 162, 74, 0.3)',
    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  ctaSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 32px',
    background: 'transparent',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '50px',
    cursor: 'pointer',
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 500,
    fontSize: '0.9rem',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
  },
  trustRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    marginTop: '48px',
    flexWrap: 'wrap',
  },
  trustItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  trustNumber: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#fff',
  },
  trustLabel: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '0.75rem',
    fontWeight: 400,
    letterSpacing: '0.1em',
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
  },
  trustDivider: {
    width: '1px',
    height: '40px',
    background: 'rgba(255,255,255,0.1)',
  },
  sliderWrapper: {
    zIndex: 1,
  },
};

export default Hero;
